import { Fragment } from "react";

function TitleBlock({ text, subtitle }) {
  return (
    <header className="block block--title">
      <h2 className="block__title">{text}</h2>
      {subtitle ? <p className="block__subtitle">{subtitle}</p> : null}
    </header>
  );
}

function ParagraphsBlock({ items }) {
  return (
    <div className="block block--paragraphs">
      {items.map((p) => (
        <p key={p} className="block__p">
          {p}
        </p>
      ))}
    </div>
  );
}

function BulletsBlock({ title, items }) {
  return (
    <div className="block block--bullets">
      {title ? <h3 className="block__h3">{title}</h3> : null}
      <ul className="block__ul">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function TagsBlock({ title, items }) {
  return (
    <div className="block block--tags">
      {title ? <h3 className="block__h3">{title}</h3> : null}
      <ul className="tag-list" aria-label={title ?? "Tags"}>
        {items.map((t) => (
          <li key={t} className="tag-list__item">
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AchievementsBlock({ items }) {
  return (
    <ul className="block block--achievements">
      {items.map((item) => (
        <li key={item} className="block__achievement">
          {item}
        </li>
      ))}
    </ul>
  );
}

function ContactStripBlock({
  portfolioUrl,
  instagramUrl,
  instagramHandle,
  email,
  phoneDisplay,
  whatsappUrl,
  telegramUrl,
  telegramHandle,
}) {
  const telHref = `tel:${phoneDisplay.replace(/\s/g, "")}`;
  const mailHref = `mailto:${email}`;

  const Icon = ({ name }) => {
    switch (name) {
      case "globe":
        return (
          <svg viewBox="0 0 24 24" className="contact-strip__icon" aria-hidden>
            <path
              fill="currentColor"
              d="M12 2a10 10 0 1 0 0 20a10 10 0 0 0 0-20Zm7.5 9h-3.06a15.6 15.6 0 0 0-1.31-5.02A8.03 8.03 0 0 1 19.5 11ZM12 4c.83 0 2.2 1.63 3.07 7H8.93C9.8 5.63 11.17 4 12 4ZM4.5 13h3.06c.22 1.83.72 3.62 1.31 5.02A8.03 8.03 0 0 1 4.5 13Zm3.06-2H4.5a8.03 8.03 0 0 1 4.37-5.02A15.6 15.6 0 0 0 7.56 11Zm1.37 2h6.14c-.87 5.37-2.24 7-3.07 7s-2.2-1.63-3.07-7Zm7.51 5.02c.59-1.4 1.09-3.19 1.31-5.02h3.06a8.03 8.03 0 0 1-4.37 5.02ZM16.44 11H7.56c.24-1.83.78-3.6 1.41-4.92C9.85 4.67 11 4 12 4s2.15.67 3.03 2.08c.63 1.32 1.17 3.09 1.41 4.92Z"
            />
          </svg>
        );
      case "instagram":
        return (
          <svg viewBox="0 0 24 24" className="contact-strip__icon" aria-hidden>
            <path
              fill="currentColor"
              d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm10 2H7a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3Zm-5 4.2A3.8 3.8 0 1 1 8.2 12A3.8 3.8 0 0 1 12 8.2Zm0 2A1.8 1.8 0 1 0 13.8 12A1.8 1.8 0 0 0 12 10.2ZM18 6.5a1 1 0 1 1-1 1a1 1 0 0 1 1-1Z"
            />
          </svg>
        );
      case "mail":
        return (
          <svg viewBox="0 0 24 24" className="contact-strip__icon" aria-hidden>
            <path
              fill="currentColor"
              d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 4.2l-8 4.8l-8-4.8V6l8 4.8L20 6v2.2Z"
            />
          </svg>
        );
      case "phone":
        return (
          <svg viewBox="0 0 24 24" className="contact-strip__icon" aria-hidden>
            <path
              fill="currentColor"
              d="M6.6 10.8a15.6 15.6 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.33.57 3.58.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.61 21 3 13.39 3 4a1 1 0 0 1 1-1h3.09a1 1 0 0 1 1 1c0 1.25.2 2.46.57 3.58a1 1 0 0 1-.24 1.02L6.6 10.8Z"
            />
          </svg>
        );
      case "whatsapp":
        return (
          <svg viewBox="0 0 24 24" className="contact-strip__icon" aria-hidden>
            <path
              fill="currentColor"
              d="M20.5 3.5A11 11 0 0 0 3.6 17.9L3 22l4.2-1.1A11 11 0 0 0 22 12a10.9 10.9 0 0 0-1.5-8.5ZM12 21a9 9 0 0 1-4.6-1.3l-.3-.2l-2.5.7l.7-2.4l-.2-.3A9 9 0 1 1 12 21Zm5.2-6.8c-.3-.2-1.7-.8-2-.9s-.5-.2-.7.2s-.8.9-1 .1c-.2-.3-.9-.5-1.8-1.4c-.7-.7-1.2-1.6-1.3-1.8c-.1-.2 0-.4.1-.5l.3-.3c.1-.1.2-.2.3-.4s0-.4 0-.5c0-.2-.7-1.7-.9-2.3c-.2-.6-.4-.5-.7-.5H8.1c-.2 0-.5.1-.7.3c-.2.2-.9.8-.9 2.1c0 1.2.9 2.4 1 2.6c.1.2 1.8 2.8 4.4 3.9c.6.3 1.1.5 1.5.6c.6.2 1.2.2 1.6.1c.5-.1 1.7-.7 1.9-1.3c.2-.6.2-1.1.1-1.3c0-.1-.2-.2-.4-.3Z"
            />
          </svg>
        );
      case "telegram":
        return (
          <svg viewBox="0 0 24 24" className="contact-strip__icon" aria-hidden>
            <path
              fill="currentColor"
              d="M21.9 4.6c.2-.8-.7-1.5-1.4-1.2L2.9 10.4c-.9.3-.9 1.6 0 1.9l4.5 1.5l1.7 5.4c.2.8 1.2 1 1.7.4l2.6-3.1l4.9 3.6c.7.5 1.6.1 1.8-.7l1.8-14.8ZM9.8 13.3l8.6-6.7c.2-.2.5.1.3.3l-7 7.5l-.3 3.4l-1.6-5.1Z"
            />
          </svg>
        );
    }
  };

  return (
    <div className="block block--contact-strip">
      <ul className="contact-strip__list">
        <li>
          <a href={instagramUrl} target="_blank" rel="noreferrer noopener" className="contact-strip__chip">
            <Icon name="instagram" />
            {instagramHandle}
          </a>
        </li>
        <li>
          <a href={whatsappUrl} target="_blank" rel="noreferrer noopener" className="contact-strip__chip">
            <Icon name="whatsapp" />
            WhatsApp
          </a>
        </li>
        <li>
          <a href={telegramUrl} target="_blank" rel="noreferrer noopener" className="contact-strip__chip">
            <Icon name="telegram" />
            @{telegramHandle}
          </a>
        </li>
        <li>
          <a href={mailHref} className="contact-strip__chip">
            <Icon name="mail" />
            {email}
          </a>
        </li>
        <li>
          <a href={telHref} className="contact-strip__chip">
            <Icon name="phone" />
            {phoneDisplay}
          </a>
        </li>
        <li>
          <a href={portfolioUrl} target="_blank" rel="noreferrer noopener" className="contact-strip__chip">
            <Icon name="globe" />
            Developer portfolio
          </a>
        </li>
      </ul>
    </div>
  );
}

function LinksBlock({ title, links }) {
  return (
    <div className="block block--links">
      {title ? <h3 className="block__h3">{title}</h3> : null}
      <ul className="link-list">
        {links.map((l) => {
          const external = /^https?:\/\//i.test(l.href);
          return (
            <li key={l.href}>
              <a
                href={l.href}
                className="link-list__a"
                {...(external ? { target: "_blank", rel: "noreferrer noopener" } : {})}
              >
                {l.label}
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function renderSlideBlock(b) {
  switch (b.type) {
    case "title":
      return <TitleBlock text={b.text} subtitle={b.subtitle} />;
    case "paragraphs":
      return <ParagraphsBlock items={b.items} />;
    case "bullets":
      return <BulletsBlock title={b.title} items={b.items} />;
    case "tags":
      return <TagsBlock title={b.title} items={b.items} />;
    case "achievements":
      return <AchievementsBlock items={b.items} />;
    case "links":
      return <LinksBlock title={b.title} links={b.links} />;
    case "columns":
      return (
        <div className="block block--columns">
          <div className="block--columns__col">
            {b.left.map((bl, i) => (
              <Fragment key={`L${i}`}>{renderSlideBlock(bl)}</Fragment>
            ))}
          </div>
          <div className="block--columns__col">
            {b.right.map((br, i) => (
              <Fragment key={`R${i}`}>{renderSlideBlock(br)}</Fragment>
            ))}
          </div>
        </div>
      );
    case "contactStrip":
      return (
        <ContactStripBlock
          portfolioUrl={b.portfolioUrl}
          instagramUrl={b.instagramUrl}
          instagramHandle={b.instagramHandle}
          email={b.email}
          phoneDisplay={b.phoneDisplay}
          whatsappUrl={b.whatsappUrl}
          telegramUrl={b.telegramUrl}
          telegramHandle={b.telegramHandle}
        />
      );
    default:
      return null;
  }
}

export function SlideBlocks({ blocks }) {
  return (
    <div className="slide-blocks">
      {blocks.map((b, i) => (
        <Fragment key={`${i}-${b.type}`}>{renderSlideBlock(b)}</Fragment>
      ))}
    </div>
  );
}
